import { type CardFields, cardFields } from "@mn/shared";
import { useMemo, useRef, useSyncExternalStore } from "react";
import type { Awareness } from "y-protocols/awareness";
import * as Y from "yjs";

/** Scalar fields of one card; re-renders only when THIS card's map changes. */
export function useCard(ymap: Y.Map<unknown>): CardFields {
  const cache = useRef<{ v: number; obj: CardFields } | null>(null);
  const version = useRef(0);
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = () => {
        version.current++;
        cb();
      };
      ymap.observe(h);
      return () => ymap.unobserve(h);
    },
    [ymap],
  );
  const owner = useRef<Y.Map<unknown> | null>(null);
  return useSyncExternalStore(subscribe, () => {
    if (owner.current !== ymap || !cache.current || cache.current.v !== version.current) {
      owner.current = ymap;
      cache.current = { v: version.current, obj: cardFields(ymap) };
    }
    return cache.current.obj;
  });
}

/** Ids in a Y.Map; re-renders only on add/remove. */
// biome-ignore lint/suspicious/noExplicitAny: accepts any Y.Map value type
export function useMapKeys(ymap: Y.Map<any>): string[] {
  const cache = useRef<{ v: number; keys: string[] }>({ v: -1, keys: [] });
  const version = useRef(0);
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = (e: Y.YMapEvent<unknown>) => {
        for (const [, ch] of e.changes.keys) {
          if (ch.action !== "update") {
            version.current++;
            cb();
            return;
          }
        }
      };
      ymap.observe(h);
      return () => ymap.unobserve(h);
    },
    [ymap],
  );
  const owner = useRef<Y.Map<unknown> | null>(null);
  return useSyncExternalStore(subscribe, () => {
    if (owner.current !== ymap || cache.current.v !== version.current) {
      owner.current = ymap;
      cache.current = { v: version.current, keys: [...ymap.keys()] };
    }
    return cache.current.keys;
  });
}

export function useYField<T>(ymap: Y.Map<unknown>, key: string): T {
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = (e: Y.YMapEvent<unknown>) => {
        if (e.keysChanged.has(key)) cb();
      };
      ymap.observe(h);
      return () => ymap.unobserve(h);
    },
    [ymap, key],
  );
  return useSyncExternalStore(subscribe, () => ymap.get(key) as T);
}

export interface PresenceState {
  user?: { id: string; name: string; color: string; image: string | null };
  pointer?: { x: number; y: number } | null;
  selection?: string[];
  editing?: string | null;
  dragging?: { id: string; x: number; y: number }[] | null;
}

/** Other clients' awareness states (excludes self). */
export function useOthers(awareness: Awareness): Map<number, PresenceState> {
  const cache = useRef<{ v: number; map: Map<number, PresenceState> }>({ v: -1, map: new Map() });
  const version = useRef(0);
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = () => {
        version.current++;
        cb();
      };
      awareness.on("change", h);
      return () => awareness.off("change", h);
    },
    [awareness],
  );
  const owner = useRef<Awareness | null>(null);
  return useSyncExternalStore(subscribe, () => {
    if (owner.current !== awareness || cache.current.v !== version.current) {
      owner.current = awareness;
      const map = new Map<number, PresenceState>();
      awareness.getStates().forEach((st, id) => {
        if (id !== awareness.clientID && st.user) map.set(id, st as PresenceState);
      });
      cache.current = { v: version.current, map };
    }
    return cache.current.map;
  });
}

/** Sorted child ids of a column; re-renders when membership or order changes anywhere in the cards map. */
export function useColumnChildren(cards: Y.Map<Y.Map<unknown>>, columnId: string): string[] {
  const cache = useRef<{ v: number; ids: string[] }>({ v: -1, ids: [] });
  const version = useRef(0);
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = (events: Y.YEvent<Y.Map<unknown>>[]) => {
        for (const e of events) {
          if (e.target === cards) {
            version.current++;
            cb();
            return;
          }
          const keys = (e as Y.YMapEvent<unknown>).keysChanged;
          if (keys && (keys.has("parent") || keys.has("order"))) {
            version.current++;
            cb();
            return;
          }
        }
      };
      cards.observeDeep(h);
      return () => cards.unobserveDeep(h);
    },
    [cards],
  );
  const owner = useRef<Y.Map<Y.Map<unknown>> | null>(null);
  return useSyncExternalStore(subscribe, () => {
    if (owner.current !== cards || cache.current.v !== version.current) {
      owner.current = cards;
      const ids: { id: string; order: string }[] = [];
      cards.forEach((c, id) => {
        if (c.get("parent") === columnId) ids.push({ id, order: (c.get("order") as string) ?? "" });
      });
      ids.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
      cache.current = { v: version.current, ids: ids.map((x) => x.id) };
    }
    return cache.current.ids;
  });
}

/** Ids of cards whose `flag` key is true; re-renders on add/remove or when that key changes on any card. */
export function useFlaggedCards(cards: Y.Map<Y.Map<unknown>>, flag: string): string[] {
  const cache = useRef<{ v: number; ids: string[] }>({ v: -1, ids: [] });
  const version = useRef(0);
  const owner = useRef<Y.Map<Y.Map<unknown>> | null>(null);
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = (events: Y.YEvent<Y.Map<unknown>>[]) => {
        for (const e of events) {
          const keys = (e as Y.YMapEvent<unknown>).keysChanged;
          if (e.target === cards || keys?.has(flag)) {
            version.current++;
            cb();
            return;
          }
        }
      };
      cards.observeDeep(h);
      return () => cards.unobserveDeep(h);
    },
    [cards, flag],
  );
  return useSyncExternalStore(subscribe, () => {
    if (owner.current !== cards || cache.current.v !== version.current) {
      owner.current = cards;
      const ids: string[] = [];
      cards.forEach((c, id) => {
        if (c.get(flag) === true) ids.push(id);
      });
      cache.current = { v: version.current, ids };
    }
    return cache.current.ids;
  });
}

/** Re-render on any nested change inside a Y type (use sparingly: small structures only). */
// biome-ignore lint/suspicious/noExplicitAny: any Y type
export function useDeepVersion(type: Y.AbstractType<any>): number {
  const v = useRef(0);
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const h = () => {
        v.current++;
        cb();
      };
      type.observeDeep(h);
      return () => type.unobserveDeep(h);
    },
    [type],
  );
  return useSyncExternalStore(subscribe, () => v.current);
}

export const isYType = (v: unknown): v is Y.AbstractType<unknown> => v instanceof Y.AbstractType;
